import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { TargetAudience } from '../../../models/MasterAdmin/pushNotificationCampaign';
import { PUSH_HUB_BASE_PATH } from '../../../models/MasterAdmin/pushNotificationHub';
import {
  NOTIFICATION_SCREEN_TYPES,
  getDefaultScreenTypeForAudience,
  getIntentValueHint,
  getScreenTypeOptionsForAudience,
} from '../../../models/MasterAdmin/notificationScreenTypes';
import {
  createPushTemplate,
  fetchPushTemplates,
  updatePushTemplate,
} from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import PushNotificationDevicePreview from './PushNotificationDevicePreview';
import PushPanelShell from './PushPanelShell';
import PushSearchSelect from './PushSearchSelect';
import { PUSH_PLACEHOLDER_HINTS, inputClass } from './pushCampaignFormShared';

const NOTIFICATION_TYPES = [
  { value: 'OFFER', label: 'Offer' },
  { value: 'DISCOUNT', label: 'Discount' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'REMINDER', label: 'Reminder' },
  { value: 'AI', label: 'AI' },
  { value: 'BLOG', label: 'Blog / content' },
  { value: 'ALERT', label: 'Alert' },
  { value: 'AWARENESS', label: 'Health day / awareness' },
];

const AUDIENCE_OPTIONS: { value: TargetAudience; label: string; group: string }[] = [
  { value: 'ALL_USERS', label: 'Registered & logged in', group: 'Users' },
  { value: 'UNREGISTERED_GUESTS', label: 'Unregistered guests', group: 'Users' },
  { value: 'INACTIVE_USERS', label: 'Inactive users', group: 'Users' },
  { value: 'LOGGED_OUT_USERS', label: 'Logged out users', group: 'Users' },
  { value: 'USERS_WITHOUT_HEALTH_RECORD', label: 'No health records', group: 'Users' },
  { value: 'SINGLE_USER', label: 'Single user', group: 'Users' },
  { value: 'DOCTORS', label: 'Doctors (all)', group: 'Doctors' },
  { value: 'INACTIVE_DOCTORS', label: 'Inactive doctors', group: 'Doctors' },
  { value: 'PENDING_APPROVAL_DOCTORS', label: 'Pending approval doctors', group: 'Doctors' },
  { value: 'LOGGED_OUT_DOCTORS', label: 'Logged-out doctors', group: 'Doctors' },
  { value: 'SINGLE_DOCTOR', label: 'Single doctor', group: 'Doctors' },
  { value: 'AMBULANCE', label: 'Ambulance (all)', group: 'Ambulance' },
  { value: 'INACTIVE_AMBULANCE', label: 'Inactive ambulance', group: 'Ambulance' },
  { value: 'PENDING_APPROVAL_AMBULANCE', label: 'Pending approval ambulance', group: 'Ambulance' },
  { value: 'LOGGED_OUT_AMBULANCE', label: 'Logged-out ambulance', group: 'Ambulance' },
  { value: 'SINGLE_AMBULANCE', label: 'Single ambulance', group: 'Ambulance' },
  { value: 'DELIVERY_PARTNERS', label: 'Delivery partners', group: 'Delivery' },
  { value: 'VENDORS', label: 'Vendors (all)', group: 'Vendors' },
  { value: 'INACTIVE_VENDORS', label: 'Inactive vendors', group: 'Vendors' },
  { value: 'LOGGED_OUT_VENDORS', label: 'Logged-out vendors', group: 'Vendors' },
  { value: 'PENDING_APPROVAL_VENDORS', label: 'Pending approval vendors', group: 'Vendors' },
  { value: 'SINGLE_VENDOR', label: 'Single vendor', group: 'Vendors' },
];

type FormState = {
  name: string;
  title: string;
  message: string;
  notificationType: string;
  intentType: string;
  intentValue: string;
  isActive: boolean;
  audienceHint: TargetAudience;
};

const emptyForm = (): FormState => ({
  name: '',
  title: '',
  message: '',
  notificationType: 'OFFER',
  intentType: 'USER_HOME',
  intentValue: '',
  isActive: true,
  audienceHint: 'ALL_USERS',
});

type ImageSlotProps = {
  label: string;
  hint: string;
  preview: string | null;
  onPick: (file: File | null) => void;
  onClear: () => void;
};

const ImageSlot: React.FC<ImageSlotProps> = ({ label, hint, preview, onPick, onClear }) => (
  <div className="rounded-xl border border-dashed border-gray-300 p-3 dark:border-gray-600">
    <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
    <p className="mt-1 text-xs text-gray-500">{hint}</p>
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm dark:border-gray-600">
        <PhotoIcon className="h-4 w-4 text-gray-500" />
        Upload
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] || null)}
        />
      </label>
      {preview ? (
        <button type="button" className="text-sm text-red-600" onClick={onClear}>
          Remove
        </button>
      ) : null}
    </div>
    {preview ? <img src={preview} alt="" className="mt-2 max-h-28 rounded-lg border object-cover" /> : null}
  </div>
);

const PushTemplateEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const isEdit = Boolean(templateId);
  const [loading, setLoading] = useState(Boolean(templateId));
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [clearImage, setClearImage] = useState(false);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [clearBanner, setClearBanner] = useState(false);

  const screenOptions = useMemo(() => {
    const scoped = getScreenTypeOptionsForAudience(form.audienceHint);
    // Also offer full catalog so all app screens remain selectable with search.
    const map = new Map(scoped.map((o) => [o.value, o]));
    for (const o of NOTIFICATION_SCREEN_TYPES) {
      if (!map.has(o.value)) map.set(o.value, o);
    }
    return Array.from(map.values());
  }, [form.audienceHint]);

  const typeOptions = NOTIFICATION_TYPES.map((t) => ({ value: t.value, label: t.label, group: 'Type' }));
  const audienceSelectOptions = AUDIENCE_OPTIONS.map((a) => ({
    value: a.value,
    label: a.label,
    group: a.group,
  }));
  const screenSelectOptions = screenOptions.map((o) => ({
    value: o.value,
    label: o.label,
    hint: o.value,
    group: o.audience.replace('_', ' '),
  }));

  useEffect(() => {
    if (!templateId) return;
    let active = true;
    setLoading(true);
    fetchPushTemplates()
      .then((res) => {
        if (!active) return;
        const row = (res.data.items || []).find((t) => t.id === templateId);
        if (!row) {
          toast.error('Template not found');
          navigate(`${PUSH_HUB_BASE_PATH}/templates`);
          return;
        }
        setForm({
          name: row.name,
          title: row.title,
          message: row.message,
          notificationType: row.notificationType,
          intentType: row.intentType,
          intentValue: row.intentValue || '',
          isActive: row.isActive,
          audienceHint: 'ALL_USERS',
        });
        setImagePreview(row.imageUrl);
        setBannerPreview(row.bannerImageUrl || null);
      })
      .catch((err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to load template')))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [templateId, navigate]);

  const insertPlaceholder = (key: string) => {
    const token = `{{${key}}}`;
    setForm((f) => ({ ...f, message: `${f.message}${f.message ? ' ' : ''}${token}` }));
  };

  const goBack = () => navigate(`${PUSH_HUB_BASE_PATH}/templates`);

  const onSave = async () => {
    if (!form.name.trim() || !form.title.trim() || !form.message.trim()) {
      toast.error('Name, title, and message are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        title: form.title.trim(),
        message: form.message.trim(),
        notificationType: form.notificationType,
        intentType: form.intentType.trim() || 'USER_HOME',
        intentValue: form.intentValue.trim(),
        isActive: form.isActive,
        image: imageFile,
        bannerImage: bannerFile,
        clearImage: Boolean(isEdit && clearImage && !imageFile),
        clearBannerImage: Boolean(isEdit && clearBanner && !bannerFile),
      };
      if (isEdit && templateId) await updatePushTemplate(templateId, payload);
      else await createPushTemplate(payload);
      toast.success(isEdit ? 'Template updated.' : 'Template created.');
      goBack();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not save template'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PushPanelShell title={isEdit ? 'Edit template' : 'New template'}>
        <p className="text-sm text-gray-500">Loading…</p>
      </PushPanelShell>
    );
  }

  return (
    <PushPanelShell
      title={isEdit ? 'Edit template' : 'New template'}
      description="Set notification content, images, and default tap destination. Campaigns only select this template."
      actions={
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm dark:border-gray-600"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div>
            <label className="mb-1 block text-sm font-medium">Internal name</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Health record reminder"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Title</label>
            <input
              className={inputClass}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Shown in tray and in-app"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Message</label>
            <textarea
              className={`${inputClass} min-h-[120px]`}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Body text. Use placeholders like {{userName}}"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {PUSH_PLACEHOLDER_HINTS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-medium dark:border-gray-600"
                >
                  {'{{'}
                  {p.key}
                  {'}}'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <ImageSlot
              label="Notification image"
              hint="Tray / lock-screen thumbnail. Recommended 96×96 to 256×256 px (1:1), PNG/JPG under 200 KB."
              preview={imagePreview}
              onPick={(file) => {
                setImageFile(file);
                setClearImage(false);
                setImagePreview(file ? URL.createObjectURL(file) : imagePreview);
              }}
              onClear={() => {
                setImageFile(null);
                setImagePreview(null);
                setClearImage(true);
              }}
            />
            <ImageSlot
              label="Banner image"
              hint="Expanded / in-app banner. Recommended 1024×512 px (2:1), JPG/PNG under 1 MB."
              preview={bannerPreview}
              onPick={(file) => {
                setBannerFile(file);
                setClearBanner(false);
                setBannerPreview(file ? URL.createObjectURL(file) : bannerPreview);
              }}
              onClear={() => {
                setBannerFile(null);
                setBannerPreview(null);
                setClearBanner(true);
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Notification type</label>
            <PushSearchSelect
              value={form.notificationType}
              onChange={(v) => setForm((f) => ({ ...f, notificationType: v || 'OFFER' }))}
              options={typeOptions}
              allowEmpty={false}
              searchPlaceholder="Search type…"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Default audience (for screen list)</label>
            <PushSearchSelect
              value={form.audienceHint}
              onChange={(v) => {
                const aud = (v || 'ALL_USERS') as TargetAudience;
                setForm((f) => ({
                  ...f,
                  audienceHint: aud,
                  intentType: getDefaultScreenTypeForAudience(aud),
                }));
              }}
              options={audienceSelectOptions}
              allowEmpty={false}
              searchPlaceholder="Search audience…"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Default tap destination</label>
            <p className="mb-2 text-xs text-gray-500">
              All app screen targets are listed. Audience above prioritizes the relevant group; search to find any screen.
            </p>
            <PushSearchSelect
              value={form.intentType}
              onChange={(v) => setForm((f) => ({ ...f, intentType: v || 'USER_HOME' }))}
              options={screenSelectOptions}
              allowEmpty={false}
              searchPlaceholder="Search screen / intent…"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Default intent value (optional)</label>
            <input
              className={inputClass}
              value={form.intentValue}
              onChange={(e) => setForm((f) => ({ ...f, intentValue: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-500">{getIntentValueHint(form.intentType)}</p>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            Active (shown in campaign picker)
          </label>

          <div className="flex gap-2 border-t pt-4 dark:border-gray-700">
            <button type="button" onClick={goBack} className="rounded-xl border px-4 py-2.5 text-sm dark:border-gray-600">
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void onSave()}
              className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </div>

        <aside className="xl:sticky xl:top-4 xl:self-start">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="mb-3 text-sm font-semibold">Mobile preview</p>
            <PushNotificationDevicePreview
              title={form.title}
              message={form.message}
              imageUrl={imagePreview}
              bannerImageUrl={bannerPreview}
            />
          </div>
        </aside>
      </div>
    </PushPanelShell>
  );
};

export default PushTemplateEditorPage;
